/**
 * TimingDisplay - Shows response timing information after each chat response
 * Displays total time, tool execution breakdown, performance metrics,
 * timeout/fallback indicators, and parallel execution info.
 */

import { useState } from 'react'
import { Clock, ChevronDown, ChevronUp, Zap, Database, Layers } from 'lucide-react'

/**
 * Format milliseconds to a human-readable string
 */
function formatDuration(ms) {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`
  }
  return `${(ms / 1000).toFixed(2)}s`
}

/**
 * TimingDisplay component
 * Shows timing information for a chat response
 *
 * @param {Object} props
 * @param {Object} props.timing - Timing data from the backend
 * @param {number} props.timing.total_ms - Total response time in milliseconds
 * @param {number} props.timing.tool_time_ms - Sum of all tool durations
 * @param {number} props.timing.tool_wall_ms - Actual parallel wall time (max tool)
 * @param {number} props.timing.ai_time_ms - Time spent on AI processing
 * @param {number} props.timing.tool_count - Number of tools executed
 * @param {boolean} props.timing.parallel - Whether tools ran in parallel
 * @param {Array} props.timing.tools - Array of tool timing details
 */
export default function TimingDisplay({ timing }) {
  const [expanded, setExpanded] = useState(false)

  if (!timing || typeof timing.total_ms !== 'number') {
    return null
  }

  const {
    total_ms,
    tool_time_ms,
    tool_wall_ms,
    ai_time_ms,
    tool_count,
    tools = [],
    parallel = false,
  } = timing

  // Determine performance indicator
  const getPerformanceColor = (ms) => {
    if (ms < 2000) return 'text-green-600'
    if (ms < 5000) return 'text-yellow-600'
    return 'text-orange-600'
  }

  const performanceColor = getPerformanceColor(total_ms)

  // Check if any tools had issues
  const hasTimeouts = tools.some(t => t.timeout)
  const hasFallbacks = tools.some(t => t.fallback_used)

  return (
    <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
      {/* Main timing row - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Total time */}
          <div className="flex items-center gap-1">
            <Clock size={12} className={performanceColor} />
            <span className={performanceColor + ' font-medium'}>
              {formatDuration(total_ms)}
            </span>
          </div>

          {/* Tool count */}
          {tool_count > 0 && (
            <div className="flex items-center gap-1">
              <Database size={12} />
              <span>{tool_count} {tool_count === 1 ? 'query' : 'queries'}</span>
            </div>
          )}

          {/* Parallel indicator */}
          {parallel && tool_count > 1 && (
            <div className="flex items-center gap-1 text-purple-500">
              <Layers size={12} />
              <span>parallel</span>
            </div>
          )}

          {/* Performance indicator */}
          {total_ms < 2000 && !hasTimeouts && (
            <div className="flex items-center gap-1 text-green-600">
              <Zap size={12} />
              <span>Fast</span>
            </div>
          )}

          {/* Timeout warning */}
          {hasTimeouts && (
            <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded font-medium">
              {hasFallbacks ? 'TIMEOUT → fallback' : 'TIMEOUT'}
            </span>
          )}
        </div>

        {/* Expand/collapse toggle */}
        {tool_count > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-gray-400">Details</span>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </div>
        )}
      </button>

      {/* Expanded details */}
      {expanded && tool_count > 0 && (
        <div className="mt-2 pl-4 space-y-1.5 text-xs">
          {/* Time breakdown */}
          <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
            <span>AI Processing: {formatDuration(ai_time_ms)}</span>
            {typeof tool_wall_ms === 'number' && tool_count > 1 ? (
              <span>Data Queries: {formatDuration(tool_wall_ms)} wall / {formatDuration(tool_time_ms)} total</span>
            ) : (
              <span>Data Queries: {formatDuration(tool_time_ms)}</span>
            )}
          </div>

          {/* Individual tool timings */}
          <div className="mt-2 space-y-1">
            <div className="text-gray-500 dark:text-gray-400 font-medium mb-1">Query Breakdown:</div>
            {tools.map((tool, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-gray-500 dark:text-gray-400 pl-2 py-0.5"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-gray-600 dark:text-gray-300">
                    {tool.tool.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                  {tool.timeout && (
                    <span className="text-[10px] px-1 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded font-medium">
                      TIMEOUT
                    </span>
                  )}
                  {tool.fallback_used && (
                    <span className="text-[10px] px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded font-medium">
                      ↪ fallback
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={
                    tool.timeout
                      ? 'text-orange-500'
                      : tool.success
                        ? 'text-green-600'
                        : 'text-red-500'
                  }>
                    {tool.timeout ? '⏱' : tool.success ? '✓' : '✗'}
                  </span>
                  <span className="w-16 text-right font-medium">
                    {formatDuration(tool.duration_ms)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Average time per tool + parallel savings */}
          {tools.length > 1 && (
            <div className="text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <span>Average: {formatDuration(tool_time_ms / tools.length)} per query</span>
              {parallel && typeof tool_wall_ms === 'number' && tool_time_ms > tool_wall_ms * 1.2 && (
                <span className="text-purple-500">
                  Parallel saved {formatDuration(tool_time_ms - tool_wall_ms)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
